import { ChangeEvent, Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, LinearProgress, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import CustomButton from '../../../../../common/CustomButton';
import CustomReactDatePicker from '../../../../../common/CustomReactDatePicker';
import CustomTimePicker from '../../../../../common/CustomTimePicker';
import { MergePreviewStopRef, MergeTripPreviewResponse } from '../../../../../types/trip.types';
import {
  buildConflictWizardSteps,
  extractStopTimeRange,
  findDerivedScheduleOverlapPairs,
  formatMergePreviewStopLabel,
  fromDateTimeLocal,
  mergeDerivedOverlapStepsIntoWizard,
  mergePreviewStopDetailLines,
  mergePreviewHasConflicts,
  normalizeDuplicateGroups,
  normalizeOverlaps,
  normalizeReminderConflicts,
  toDateTimeLocal,
} from '../../../../../utils/mergeTripPreview';
import type { MergeConflictWizardStep } from '../../../../../utils/mergeTripPreview';
import styles from './MergeTripsModal.module.scss';

function stopDisplayTitle(stop: MergePreviewStopRef): string {
  return stop.title?.trim() || stop.name?.trim() || formatMergePreviewStopLabel(stop).split(' · ')[0];
}

function stopPrimaryLine(stop: MergePreviewStopRef): string {
  return stop.stop_type ? String(stop.stop_type) : 'Stop';
}

function pairToken(leftId: number, rightId: number): string {
  return [leftId, rightId].sort((a, b) => a - b).join('|');
}

export type TimeDraft = {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
};

function isoToDraftParts(iso?: string): { d: string; t: string } {
  if (!iso) return { d: '', t: '' };
  const local = toDateTimeLocal(iso);
  if (!local) return { d: '', t: '' };
  const [d, rest] = local.split('T');
  const t = rest?.slice(0, 5) ?? '';
  return { d: d || '', t };
}

function snapTimeToFiveMinutes(hhmm: string): string {
  if (!/^\d{2}:\d{2}$/.test(hhmm)) return hhmm;
  const [hs, ms] = hhmm.split(':');
  const total = parseInt(hs, 10) * 60 + parseInt(ms, 10);
  const snapped = Math.round(total / 5) * 5;
  const nh = Math.floor(snapped / 60) % 24;
  const nm = ((snapped % 60) + 60) % 60;
  return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
}

function draftFromStop(stop: MergePreviewStopRef): TimeDraft {
  const { start, end } = extractStopTimeRange(stop);
  const a = isoToDraftParts(start);
  const b = isoToDraftParts(end);
  return {
    startDate: a.d,
    startTime: snapTimeToFiveMinutes(a.t),
    endDate: b.d,
    endTime: snapTimeToFiveMinutes(b.t),
  };
}

function draftToPayload(draft: TimeDraft): { startIso: string; endIso: string } | null {
  const { startDate, startTime, endDate, endTime } = draft;
  if (!startDate || !startTime || !endDate || !endTime) return null;
  try {
    const startIso = fromDateTimeLocal(`${startDate}T${startTime}`);
    const endIso = fromDateTimeLocal(`${endDate}T${endTime}`);
    return { startIso, endIso };
  } catch {
    return null;
  }
}

type ResolutionMode = null | 'keep-one' | 'time-edit';

type MergeTripReviewDialogProps = {
  open: boolean;
  targetTripName: string;
  tripStartDate: string;
  tripEndDate: string;
  preview: MergeTripPreviewResponse;
  onResolvePair: (keepStopId: number, discardStopId: number) => void;
  onKeepBothInPair: (leftId: number, rightId: number) => void;
  onBack: () => void;
  onFinalize: (discardStopIdsOverride?: number[]) => void;
  onQueueScheduleChange: (params: { stopId: number; stopType?: string; startIso: string; endIso: string }) => void;
  isSubmitting: boolean;
  error?: string | null;
  tripNamesById?: Record<number, string>;
};

const MergeTripReviewDialog: FC<MergeTripReviewDialogProps> = ({
  open,
  targetTripName,
  tripStartDate,
  tripEndDate,
  preview,
  onResolvePair,
  onKeepBothInPair,
  onBack,
  onFinalize,
  onQueueScheduleChange,
  isSubmitting,
  error,
  tripNamesById,
}) => {
  const baseWizardSteps = useMemo(() => buildConflictWizardSteps(preview), [preview]);

  const [stepIndex, setStepIndex] = useState(0);
  const [resolvedFlag, setResolvedFlag] = useState(false);
  const [resolutionMode, setResolutionMode] = useState<ResolutionMode>(null);
  const [keepSide, setKeepSide] = useState<'left' | 'right' | null>(null);

  const [draftLeft, setDraftLeft] = useState<TimeDraft>(() => ({
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
  }));
  const [draftRight, setDraftRight] = useState<TimeDraft>(() => ({
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
  }));
  const [saveErrorLeft, setSaveErrorLeft] = useState<string | null>(null);
  const [saveErrorRight, setSaveErrorRight] = useState<string | null>(null);
  const [introSubtitleDismissed, setIntroSubtitleDismissed] = useState(false);
  const [discardedStopIdsLocal, setDiscardedStopIdsLocal] = useState<Set<number>>(new Set());
  const [timeAdjustedStopIdsLocal, setTimeAdjustedStopIdsLocal] = useState<Set<number>>(new Set());
  const [sessionMaxPairSteps, setSessionMaxPairSteps] = useState(0);

  const steps = useMemo(
    () => mergeDerivedOverlapStepsIntoWizard(preview, discardedStopIdsLocal, baseWizardSteps),
    [preview, discardedStopIdsLocal, baseWizardSteps]
  );

  const derivedOverlapPairTokens = useMemo(() => {
    const pairs = findDerivedScheduleOverlapPairs(preview, discardedStopIdsLocal);
    return new Set(pairs.map(p => pairToken(p.left.id, p.right.id)));
  }, [preview, discardedStopIdsLocal]);

  const pairSteps = useMemo(
    () => steps.filter((s): s is Extract<MergeConflictWizardStep, { kind: 'pair' }> => s.kind === 'pair'),
    [steps]
  );

  const hasLegacyFallback = useMemo(() => {
    if (steps.length > 0) return false;
    const dups = normalizeDuplicateGroups(preview);
    const ovs = normalizeOverlaps(preview);
    const rem = normalizeReminderConflicts(preview);
    return mergePreviewHasConflicts(preview) && !dups.some(g => g.length >= 2) && ovs.length === 0 && rem.length === 0;
  }, [preview, steps.length]);

  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  const baselineLeftRef = useRef('');
  const baselineRightRef = useRef('');
  const skipAutosaveRef = useRef(false);
  const sawServerConflictsRef = useRef(false);
  const autoFinalizeOnceRef = useRef(false);

  useEffect(() => {
    if (open) {
      setStepIndex(0);
      setResolvedFlag(false);
      setResolutionMode(null);
      setKeepSide(null);
      setSaveErrorLeft(null);
      setSaveErrorRight(null);
      setIntroSubtitleDismissed(false);
      setDiscardedStopIdsLocal(new Set());
      setTimeAdjustedStopIdsLocal(new Set());
      setSessionMaxPairSteps(0);
      sawServerConflictsRef.current = false;
      autoFinalizeOnceRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (open && error) {
      autoFinalizeOnceRef.current = false;
    }
  }, [open, error]);

  useEffect(() => {
    if (!open) return;
    setSessionMaxPairSteps(prev => Math.max(prev, pairSteps.length));
  }, [open, pairSteps.length]);

  const safeIndex = Math.min(stepIndex, Math.max(steps.length - 1, 0));
  const currentStep = steps[safeIndex];
  const pairKey = currentStep?.kind === 'pair' ? `${currentStep.left.id}:${currentStep.right.id}` : '';
  const isLastStep = steps.length > 0 && safeIndex >= steps.length - 1;

  useEffect(() => {
    if (resolvedFlag && currentStep?.kind === 'pair') {
      setIntroSubtitleDismissed(true);
    }
  }, [resolvedFlag, currentStep]);

  useEffect(() => {
    if (!pairKey) return;
    const idx = Math.min(stepIndex, Math.max(stepsRef.current.length - 1, 0));
    const step = stepsRef.current[idx];
    if (step?.kind !== 'pair') return;

    const dl = draftFromStop(step.left);
    const dr = draftFromStop(step.right);
    setDraftLeft(dl);
    setDraftRight(dr);
    baselineLeftRef.current = JSON.stringify(dl);
    baselineRightRef.current = JSON.stringify(dr);
    skipAutosaveRef.current = true;
    requestAnimationFrame(() => {
      skipAutosaveRef.current = false;
    });

    setResolvedFlag(false);
    setResolutionMode(null);
    setKeepSide(null);
    setSaveErrorLeft(null);
    setSaveErrorRight(null);
  }, [stepIndex, pairKey]);

  const tripWindow = useMemo(() => {
    const minDateTime = `${tripStartDate}T00:00`;
    const maxDateTime = `${tripEndDate}T23:59`;
    return { minDateTime, maxDateTime };
  }, [tripStartDate, tripEndDate]);

  const validatePayload = useCallback(
    (startIso: string, endIso: string): string | null => {
      if (new Date(startIso).getTime() >= new Date(endIso).getTime()) {
        return 'End must be after start.';
      }
      if (
        new Date(startIso).getTime() < new Date(tripWindow.minDateTime).getTime() ||
        new Date(endIso).getTime() > new Date(tripWindow.maxDateTime).getTime()
      ) {
        return `Times must fall within trip dates (${tripStartDate} – ${tripEndDate}).`;
      }
      return null;
    },
    [tripEndDate, tripStartDate, tripWindow.maxDateTime, tripWindow.minDateTime]
  );

  const runFinalize = useCallback(
    (discardStopIdsOverride?: number[]) => {
      if (autoFinalizeOnceRef.current) return;
      autoFinalizeOnceRef.current = true;
      onFinalize(discardStopIdsOverride);
    },
    [onFinalize]
  );

  const [scheduleOverlapBlock, setScheduleOverlapBlock] = useState<string | null>(null);

  const isPairStepSkipped = useCallback(
    (
      step: Extract<MergeConflictWizardStep, { kind: 'pair' }>,
      discarded: Set<number>,
      adjusted: Set<number>,
      overlapTokens: Set<string>
    ) => {
      const token = pairToken(step.left.id, step.right.id);
      if (overlapTokens.has(token)) return false;
      return (
        discarded.has(step.left.id) ||
        discarded.has(step.right.id) ||
        adjusted.has(step.left.id) ||
        adjusted.has(step.right.id)
      );
    },
    []
  );

  const findNextActionableStep = useCallback(
    (fromIndex: number, discarded: Set<number>, adjusted: Set<number>): number => {
      for (let i = fromIndex; i < steps.length; i += 1) {
        const step = steps[i];
        if (!step) continue;
        if (step.kind !== 'pair') {
          return i;
        }
        if (!isPairStepSkipped(step, discarded, adjusted, derivedOverlapPairTokens)) {
          return i;
        }
      }
      return -1;
    },
    [derivedOverlapPairTokens, isPairStepSkipped, steps]
  );

  const safeFinalize = useCallback(
    (opts?: { discardedStopIds?: Set<number> }) => {
      const effectiveDiscarded = opts?.discardedStopIds ?? discardedStopIdsLocal;
      const overlaps = findDerivedScheduleOverlapPairs(preview, effectiveDiscarded);
      if (overlaps.length > 0) {
        autoFinalizeOnceRef.current = false;
        const nextIdx = findNextActionableStep(0, effectiveDiscarded, timeAdjustedStopIdsLocal);
        if (nextIdx >= 0) {
          setScheduleOverlapBlock(
            'Resolve the overlapping stops below (new overlap after your time changes), then continue.'
          );
          setResolvedFlag(false);
          setResolutionMode(null);
          setKeepSide(null);
          setStepIndex(nextIdx);
          return;
        }
        setScheduleOverlapBlock(
          'After your time changes, two or more stops still overlap. Adjust the times so they do not overlap, then try merging again.'
        );
        return;
      }
      setScheduleOverlapBlock(null);
      runFinalize(Array.from(effectiveDiscarded));
    },
    [discardedStopIdsLocal, findNextActionableStep, preview, runFinalize, timeAdjustedStopIdsLocal]
  );

  useEffect(() => {
    if (findDerivedScheduleOverlapPairs(preview, discardedStopIdsLocal).length === 0) {
      setScheduleOverlapBlock(null);
    }
  }, [preview, discardedStopIdsLocal]);

  useEffect(() => {
    if (!open) return;
    if (mergePreviewHasConflicts(preview)) {
      sawServerConflictsRef.current = true;
      return;
    }
    if (findDerivedScheduleOverlapPairs(preview, discardedStopIdsLocal).length > 0) {
      return;
    }
    if (!sawServerConflictsRef.current) return;
    safeFinalize();
  }, [open, preview, safeFinalize, discardedStopIdsLocal]);

  const currentPairToken = currentStep?.kind === 'pair' ? pairToken(currentStep.left.id, currentStep.right.id) : '';

  const actionablePairSteps = useMemo(
    () =>
      pairSteps.filter(step => {
        const token = pairToken(step.left.id, step.right.id);
        if (token === currentPairToken) return true;
        return !isPairStepSkipped(step, discardedStopIdsLocal, timeAdjustedStopIdsLocal, derivedOverlapPairTokens);
      }),
    [
      currentPairToken,
      derivedOverlapPairTokens,
      discardedStopIdsLocal,
      isPairStepSkipped,
      pairSteps,
      timeAdjustedStopIdsLocal,
    ]
  );

  const pairOrdinal =
    currentStep?.kind === 'pair'
      ? actionablePairSteps.findIndex(p => pairToken(p.left.id, p.right.id) === currentPairToken) + 1
      : 0;

  const globalPairIndex =
    currentStep?.kind === 'pair' && sessionMaxPairSteps > 0
      ? sessionMaxPairSteps - actionablePairSteps.length + Math.max(pairOrdinal, 1)
      : 0;

  const globalPairTotal = sessionMaxPairSteps;

  const showProgress = actionablePairSteps.length > 1 || sessionMaxPairSteps > 1;

  const progressValue =
    !showProgress || globalPairTotal <= 0
      ? 100
      : currentStep?.kind !== 'pair'
        ? 100
        : Math.min(
            100,
            ((Math.min(globalPairIndex, globalPairTotal) - 1 + (resolvedFlag ? 1 : 0.35)) / globalPairTotal) * 100
          );

  const queueValidatedSchedule = useCallback(
    (stop: MergePreviewStopRef, draft: TimeDraft, side: 'left' | 'right') => {
      if (skipAutosaveRef.current) return;
      const baseline = side === 'left' ? baselineLeftRef : baselineRightRef;
      if (JSON.stringify(draft) === baseline.current) return;

      const payload = draftToPayload(draft);
      if (!payload) return;

      const validationErr = validatePayload(payload.startIso, payload.endIso);
      if (validationErr) {
        if (side === 'left') setSaveErrorLeft(validationErr);
        else setSaveErrorRight(validationErr);
        return;
      }
      if (side === 'left') setSaveErrorLeft(null);
      else setSaveErrorRight(null);

      onQueueScheduleChange({
        stopId: stop.id,
        stopType: stop.stop_type,
        startIso: payload.startIso,
        endIso: payload.endIso,
      });
      setTimeAdjustedStopIdsLocal(prev => new Set(prev).add(stop.id));
      baseline.current = JSON.stringify(draft);
      setResolutionMode('time-edit');
      setResolvedFlag(true);
      setKeepSide(null);
    },
    [onQueueScheduleChange, validatePayload]
  );

  const leftStopId = currentStep?.kind === 'pair' ? currentStep.left.id : null;
  const rightStopId = currentStep?.kind === 'pair' ? currentStep.right.id : null;

  useEffect(() => {
    if (leftStopId == null) return;
    const idx = Math.min(stepIndex, Math.max(stepsRef.current.length - 1, 0));
    const step = stepsRef.current[idx];
    if (step?.kind !== 'pair' || step.left.id !== leftStopId) return;
    const stop = step.left;
    const timer = window.setTimeout(() => {
      queueValidatedSchedule(stop, draftLeft, 'left');
    }, 480);
    return () => window.clearTimeout(timer);
  }, [draftLeft, pairKey, leftStopId, queueValidatedSchedule, stepIndex]);

  useEffect(() => {
    if (rightStopId == null) return;
    const idx = Math.min(stepIndex, Math.max(stepsRef.current.length - 1, 0));
    const step = stepsRef.current[idx];
    if (step?.kind !== 'pair' || step.right.id !== rightStopId) return;
    const stop = step.right;
    const timer = window.setTimeout(() => {
      queueValidatedSchedule(stop, draftRight, 'right');
    }, 480);
    return () => window.clearTimeout(timer);
  }, [draftRight, pairKey, rightStopId, queueValidatedSchedule, stepIndex]);

  const handleKeepLeft = (left: MergePreviewStopRef, right: MergePreviewStopRef) => {
    onResolvePair(left.id, right.id);
    setIntroSubtitleDismissed(true);
    const nextDiscarded = new Set(discardedStopIdsLocal);
    nextDiscarded.add(right.id);
    setDiscardedStopIdsLocal(nextDiscarded);

    const nextStep = findNextActionableStep(safeIndex + 1, nextDiscarded, timeAdjustedStopIdsLocal);
    if (nextStep === -1) {
      safeFinalize({ discardedStopIds: nextDiscarded });
      return;
    }
    setStepIndex(nextStep);
  };

  const handleKeepRight = (left: MergePreviewStopRef, right: MergePreviewStopRef) => {
    onResolvePair(right.id, left.id);
    setIntroSubtitleDismissed(true);
    const nextDiscarded = new Set(discardedStopIdsLocal);
    nextDiscarded.add(left.id);
    setDiscardedStopIdsLocal(nextDiscarded);

    const nextStep = findNextActionableStep(safeIndex + 1, nextDiscarded, timeAdjustedStopIdsLocal);
    if (nextStep === -1) {
      safeFinalize({ discardedStopIds: nextDiscarded });
      return;
    }
    setStepIndex(nextStep);
  };

  const handleContinue = () => {
    if (hasLegacyFallback || steps.length === 0) {
      safeFinalize();
      return;
    }
    const step = steps[safeIndex];
    if (!step) {
      safeFinalize();
      return;
    }

    if (step.kind === 'reminders') {
      if (isLastStep) safeFinalize();
      else setStepIndex(i => i + 1);
      return;
    }

    if (step.kind === 'pair' && !resolvedFlag) {
      return;
    }

    if (step.kind === 'pair' && resolutionMode === 'time-edit') {
      onKeepBothInPair(step.left.id, step.right.id);
      const nextAdjusted = new Set(timeAdjustedStopIdsLocal);
      nextAdjusted.add(step.left.id);
      nextAdjusted.add(step.right.id);
      setTimeAdjustedStopIdsLocal(nextAdjusted);
      const nextStep = findNextActionableStep(safeIndex + 1, discardedStopIdsLocal, nextAdjusted);
      if (nextStep === -1) {
        safeFinalize();
      } else {
        setStepIndex(nextStep);
      }
      return;
    }

    if (isLastStep) {
      safeFinalize();
    } else {
      const nextStep = findNextActionableStep(safeIndex + 1, discardedStopIdsLocal, timeAdjustedStopIdsLocal);
      if (nextStep === -1) {
        safeFinalize();
      } else {
        setStepIndex(nextStep);
      }
    }
  };

  const hidePairFooterUntilResolved =
    currentStep?.kind === 'pair' && !resolvedFlag && steps.length > 0 && !hasLegacyFallback;

  const footerPrimaryLabel = (): string => {
    if (isSubmitting) return 'Merging…';
    if (steps.length === 0 || hasLegacyFallback) return 'Merge trips';
    const step = steps[safeIndex];
    if (step?.kind === 'reminders') {
      return isLastStep ? 'Merge trips' : 'Continue';
    }
    if (step?.kind === 'pair' && resolutionMode === 'time-edit' && resolvedFlag) {
      return 'Continue with keeping both';
    }
    if (step?.kind === 'pair' && resolutionMode === 'keep-one' && resolvedFlag) {
      return isLastStep ? 'Merge trips' : 'Continue';
    }
    return isLastStep ? 'Merge trips' : 'Continue';
  };

  const showFooterPrimary = !hidePairFooterUntilResolved;

  const footerDisabled = isSubmitting;

  const showIntroSubtitle = mergePreviewHasConflicts(preview) && !introSubtitleDismissed;

  const makeDraftHandler =
    (setDraft: Dispatch<SetStateAction<TimeDraft>>, field: keyof TimeDraft) => (e: ChangeEvent<HTMLInputElement>) => {
      setDraft(d => ({ ...d, [field]: e.target.value }));
    };

  const renderDateTimeGrid = (
    stop: MergePreviewStopRef,
    draft: TimeDraft,
    setDraft: Dispatch<SetStateAction<TimeDraft>>,
    saveError: string | null
  ) => {
    const endDateMin = draft.startDate || tripStartDate;

    return (
      <Box className={styles.conflictCardDateBlock}>
        <Typography
          variant="h11Bold"
          className={styles.conflictCardSubheading}
        >
          Date and Time
        </Typography>
        <Box className={styles.conflictHotelGrid}>
          <CustomReactDatePicker
            name="startDate"
            inputLabel="Start date"
            placeholder="Select date"
            value={draft.startDate}
            onChange={makeDraftHandler(setDraft, 'startDate')}
            min={tripStartDate}
            max={tripEndDate}
            disabled={isSubmitting}
            customClass={styles.conflictPickerSlot}
          />
          <CustomTimePicker
            name="startTime"
            inputLabel="Start time"
            placeholder="Select time"
            value={draft.startTime}
            onChange={makeDraftHandler(setDraft, 'startTime')}
            disabled={isSubmitting}
            hideIcon
            customClass={styles.conflictPickerSlot}
          />
          <CustomReactDatePicker
            name="endDate"
            inputLabel="End date"
            placeholder="Select date"
            value={draft.endDate}
            onChange={makeDraftHandler(setDraft, 'endDate')}
            min={endDateMin}
            max={tripEndDate}
            disabled={isSubmitting}
            customClass={styles.conflictPickerSlot}
          />
          <CustomTimePicker
            name="endTime"
            inputLabel="End time"
            placeholder="Select time"
            value={draft.endTime}
            onChange={makeDraftHandler(setDraft, 'endTime')}
            disabled={isSubmitting}
            hideIcon
            customClass={styles.conflictPickerSlot}
          />
        </Box>
        {saveError && (
          <Typography
            variant="h11"
            sx={{ color: '#C8102E', mt: 1 }}
          >
            {saveError}
          </Typography>
        )}
      </Box>
    );
  };

  const renderStopCard = (
    stop: MergePreviewStopRef,
    peerStop: MergePreviewStopRef,
    side: 'left' | 'right',
    draft: TimeDraft,
    setDraft: Dispatch<SetStateAction<TimeDraft>>,
    saveError: string | null
  ) => {
    const tid = stop.trip_id ?? stop.trip;
    const tripHeading =
      stop.trip_name?.trim() || (typeof tid === 'number' && tripNamesById ? tripNamesById[tid] : undefined);
    const metaLines = mergePreviewStopDetailLines(stop);

    return (
      <Box className={styles.conflictColumn}>
        {tripHeading ? (
          <Typography
            variant="h10Bold"
            className={styles.conflictTripTitle}
          >
            {tripHeading}
          </Typography>
        ) : null}
        <Box className={styles.conflictCard}>
          <Typography
            variant="h10Bold"
            className={styles.conflictCardName}
          >
            {stopDisplayTitle(stop)}
          </Typography>
          {metaLines.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', mb: '8px' }}>
              {metaLines.map((row, i) => (
                <Typography
                  key={`${stop.id}-meta-${row.kind}-${i}`}
                  component="p"
                  className={
                    row.kind === 'booking' || row.kind === 'place'
                      ? styles.conflictStopMetaStrong
                      : styles.conflictStopMetaRegular
                  }
                  sx={{ fontSize: '14px' }}
                >
                  {row.text}
                </Typography>
              ))}
            </Box>
          ) : null}
          {renderDateTimeGrid(stop, draft, setDraft, saveError)}
        </Box>

        {!resolvedFlag && (
          <CustomButton
            variant={keepSide === side ? 'contained' : 'outlined'}
            customClass={keepSide === side ? styles.conflictKeepBtnSelected : styles.conflictKeepBtn}
            onClick={() => (side === 'left' ? handleKeepLeft(stop, peerStop) : handleKeepRight(peerStop, stop))}
            disabled={isSubmitting}
          >
            Keep this Stop
          </CustomButton>
        )}
      </Box>
    );
  };

  return (
    <Modal
      open={open}
      onClose={onBack}
      sx={{ '& .MuiBackdrop-root': { backgroundColor: 'rgba(34, 43, 58, 0.35)' } }}
    >
      <Box className={styles.conflictWizardPaper}>
        <IconButton
          className={styles.conflictWizardClose}
          onClick={onBack}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>

        <Box className={styles.conflictWizardContent}>
          <Typography
            variant="h5Bold"
            className={styles.conflictWizardTitle}
          >
            Resolve Conflicts
          </Typography>
          {showIntroSubtitle ? (
            <Typography
              variant="h11"
              className={styles.conflictWizardSubtitle}
            >
              We found some overlapping or duplicate stops. Please review before merging.
            </Typography>
          ) : null}
        </Box>

        {showProgress && (
          <>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, progressValue)}
              className={styles.conflictProgressBar}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: '#eaeef8',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  backgroundImage: [
                    'linear-gradient(89.94deg, #E52DF5 0%, #15C3FF 100.45%)',
                    'linear-gradient(269.95deg, #15C3FF 3.54%, #8DF4DB 100%)',
                  ].join(', '),
                  backgroundBlendMode: 'screen',
                },
              }}
            />
            {currentStep?.kind === 'pair' ? (
              <Typography
                variant="h10"
                className={styles.conflictCounter}
              >
                Conflict {Math.min(Math.max(globalPairIndex, 1), globalPairTotal)} of {globalPairTotal}
              </Typography>
            ) : null}
          </>
        )}

        {(error || scheduleOverlapBlock) && (
          <Typography
            variant="h11"
            sx={{ color: '#C8102E', textAlign: 'center', width: '100%' }}
          >
            {scheduleOverlapBlock ?? error}
          </Typography>
        )}

        {hasLegacyFallback && (
          <Typography
            variant="h10"
            sx={{ color: '#5E6A81', textAlign: 'center' }}
          >
            This merge reported conflicts in a format we could not split into steps. You can still try merging — the
            server will validate the result.
          </Typography>
        )}

        {currentStep?.kind === 'pair' && (
          <>
            <Box className={styles.conflictCompareRow}>
              {renderStopCard(currentStep.left, currentStep.right, 'left', draftLeft, setDraftLeft, saveErrorLeft)}
              {renderStopCard(currentStep.right, currentStep.left, 'right', draftRight, setDraftRight, saveErrorRight)}
            </Box>
            {currentStep.message ? (
              <Typography
                variant="h11"
                sx={{ color: '#5E6A81', textAlign: 'center' }}
              >
                {currentStep.message}
              </Typography>
            ) : null}
          </>
        )}

        {currentStep?.kind === 'reminders' && (
          <Box className={styles.reminderStep}>
            {currentStep.items.map((r, i) => (
              <Typography
                key={i}
                variant="h10"
                sx={{ color: '#222B3A' }}
              >
                {r.message || r.description || `Reminder conflict ${i + 1}`}
              </Typography>
            ))}
          </Box>
        )}

        {resolvedFlag &&
          !scheduleOverlapBlock &&
          steps.length > 0 &&
          currentStep?.kind === 'pair' &&
          resolutionMode === 'time-edit' && (
            <Box className={styles.conflictResolvedBanner}>
              <CheckCircleIcon sx={{ color: '#208B64', fontSize: 22 }} />
              <Typography variant="h11Bold">Conflict is resolved</Typography>
            </Box>
          )}

        {showFooterPrimary ? (
          <Box className={styles.conflictFooterPrimaryWrap}>
            <CustomButton
              variant="contained"
              customClass={styles.conflictFooterPillPrimary}
              onClick={handleContinue}
              disabled={footerDisabled}
            >
              {footerPrimaryLabel()}
            </CustomButton>
          </Box>
        ) : null}
      </Box>
    </Modal>
  );
};

export default MergeTripReviewDialog;
